import { THEME_STORAGE } from "./constants";

export type Theme = "light" | "dark" | "system";

type Listener = (t: Theme) => void;
const listeners = new Set<Listener>();

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readStored(): Theme {
  const v = localStorage.getItem(THEME_STORAGE);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

let current: Theme = typeof window === "undefined" ? "system" : readStored();

function applyToDocument(t: Theme) {
  if (typeof document === "undefined") return;
  const isDark = t === "dark" || (t === "system" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", isDark);
}

/** Call once at app boot. */
export function initTheme(): void {
  applyToDocument(current);
  // Track OS preference changes when "system" is active.
  if (typeof window !== "undefined") {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => {
        if (current === "system") applyToDocument(current);
      });
  }
}

export function getTheme(): Theme {
  return current;
}

export function setTheme(t: Theme): void {
  current = t;
  localStorage.setItem(THEME_STORAGE, t);
  applyToDocument(t);
  for (const l of listeners) l(t);
}

export function subscribeTheme(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Quick toggle between explicit light/dark (drops "system"). */
export function toggleTheme(): void {
  const isDark = document.documentElement.classList.contains("dark");
  setTheme(isDark ? "light" : "dark");
}
